import React, { useCallback, useEffect } from 'react';

import type { UUID } from '@aline/common';
import { link } from '@aline/routes';
import { gql } from '@apollo/client';
import { Collapse, IconButton, Skeleton } from '@mui/material';
import { Settings } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { AlineTooltip, panelHeaderHeightPx } from 'src/components/generic';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { WorkflowAssigneesButton } from 'src/components/workflow/assignees';
import { Apollo } from 'src/gql/generated';
import { getTemplatePublishedInTooltip } from 'src/helpers/integration-display-name';
import { CrmIntegrationIcon } from 'src/images/integrations/CrmIntegrationIcon';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { useTabContext } from 'src/layouts/tabs/tab-context';
import { DeleteWorkflowIconButton } from 'src/workflow';
import { WorkflowLabelList } from 'src/workflow/labels';
import { makeStyles } from 'tss-react/mui';

import { BoardMetadata } from './BoardMetadata';
import { useWorkflowDetailLinks } from './WorkflowDetailContext';
import type { WorkflowDetailMeta } from './WorkflowDetailContext';

const useStyles = makeStyles()(theme => ({
  assigneesSkeleton: {
    display: 'flex',
    gap: 4,
  },
  backButton: {
    padding: theme.spacing(0.5),
  },
  crmChip: {
    alignItems: 'center',
    boxSizing: 'border-box',
    cursor: 'default',
    display: 'flex',
    flexShrink: 0,
    height: 28,
    justifyContent: 'center',
    width: 28,
  },
  crmCluster: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: 4,
  },
  crmDivider: {
    alignSelf: 'center',
    backgroundColor: theme.appColors.rule,
    flexShrink: 0,
    height: 16,
    width: 1,
  },
  crmIcon: {
    display: 'block',
    height: 18,
    objectFit: 'contain',
    width: 18,
  },
  headerAssignees: {
    boxSizing: 'border-box',
    height: 28,
    minHeight: 28,
    padding: 5,
  },
  labelsSkeleton: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  metaRow: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  titleActions: {
    '& .MuiIconButton-root': {
      boxSizing: 'border-box',
      flexShrink: 0,
      height: 28,
      minHeight: 28,
      minWidth: 28,
      padding: 5,
      width: 28,
    },
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: 4,
  },
  titleRow: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    maxHeight: panelHeaderHeightPx,
    minHeight: panelHeaderHeightPx,
    padding: theme.spacing(0.5, 1),
    width: '100%',
  },
  titleStart: {
    alignItems: 'center',
    display: 'flex',
    gap: 4,
    minWidth: 0,
  },
}));

void gql`
  query GetWorkflowHeader($workflowId: UUID!) {
    workflow(workflowId: $workflowId) {
      __typename
      aiCancelledAt
      aiFailedAt
      aiFinishedAt
      aiStartedAt
      id
      name
      state
      startedAt
      publishedAt
      variant
      priority
      skipTriage
      column {
        ...ColumnWithBoard
      }
      doneColumn {
        ...ColumnBase
      }
      labels {
        ...LabelSimple
      }
      owner {
        ...UserBase
      }
      permissions {
        ...WorkflowPermissionSimple
      }
      files {
        ...WorkflowFileSimple
      }
      comments {
        ...CommentSimple
      }
      crmIntegrations
      phases {
        __typename
        id
        name
        order
        startedAt
        completedAt
        column {
          id
        }
      }
    }
  }
`;

void gql`
  mutation UpdateWorkflowName($input: UpdateWorkflowInput!) {
    updateWorkflow(input: $input) {
      workflow {
        __typename
        id
        name
      }
    }
  }
`;

interface WorkflowHeaderProps {
  workflowId: UUID;
}

/**
 * Full-width title row: back and name on the left; CRM logos, then assignees, delete, and settings on the right.
 * Rendered outside the constrained content container so it spans the full page.
 */
export const WorkflowHeaderTitleRow = React.memo(function WorkflowHeaderTitleRow({
  workflowId,
}: WorkflowHeaderProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { updateActiveTabTitle } = useTabContext();
  const links = useWorkflowDetailLinks(workflowId);
  const { openDrawer } = useGlobalDrawer();
  const handleOpenSettings = () => {
    openDrawer();
    navigate(links.settings());
  };

  /** Kept on an anchor so back can be copied, bookmarked and opened in a new browser tab. */
  const backToRoute =
    links.workflow?.variant === Apollo.WorkflowVariant.Template
      ? links.workflow.publishedAt
        ? link.TemplatePublished()
        : link.TemplateDraft()
      : link.WorkflowList();

  const entityLabel =
    links.workflow?.variant === Apollo.WorkflowVariant.Template ? 'Template' : 'Workflow';
  const untitledFallback = `Untitled ${entityLabel}`;
  const pageTitle = links.workflow?.name ?? entityLabel;

  useEffect(() => {
    if (typeof links.workflow?.name === 'string')
      updateActiveTabTitle(links.workflow.name || untitledFallback);
  }, [links.workflow?.name, untitledFallback, updateActiveTabTitle]);

  if (!links.workflow) return <WorkflowTitleRowSkeleton />;

  const visiblePermissions = links.workflow.permissions.filter(
    getIsVisibleWorkflowHeaderPermission
  );

  return (
    <>
      <Helmet>
        <title>Aline | {pageTitle}</title>
      </Helmet>
      <div className={classes.titleRow}>
        <div className={classes.titleStart}>
          <IconButton className={classes.backButton} component={Link} to={backToRoute}>
            <BackArrowIcon />
          </IconButton>
          <WorkflowName workflow={links.workflow} />
        </div>
        <div className={classes.titleActions}>
          <WorkflowCrmLinkChips workflow={links.workflow} />
          {visiblePermissions.length > 0 && (
            <WorkflowAssigneesButton
              assignees={visiblePermissions}
              className={classes.headerAssignees}
              max={3}
              open={false}
              onClick={() => navigate(links.collaborators())}
            />
          )}
          <DeleteWorkflowIconButton workflowId={workflowId} />
          <AlineTooltip arrow placement="top" title={`${entityLabel} settings`}>
            <IconButton size="small" onClick={handleOpenSettings}>
              <Settings size={18} />
            </IconButton>
          </AlineTooltip>
        </div>
      </div>
    </>
  );
});

function getIsVisibleWorkflowHeaderPermission(
  permission: WorkflowDetailMeta['permissions'][number]
): boolean {
  return Boolean(permission.account || permission.role);
}

function WorkflowCrmLinkChips({
  workflow,
}: {
  workflow: Pick<WorkflowDetailMeta, 'crmIntegrations' | 'variant'>;
}) {
  const { classes } = useStyles();
  if (workflow.variant !== Apollo.WorkflowVariant.Template) return null;

  return (
    <Collapse
      appear
      unmountOnExit
      in={workflow.crmIntegrations.length > 0}
      orientation="horizontal"
      sx={{ '&.MuiCollapse-hidden': { display: 'none' } }}
      timeout={200}
    >
      <div className={classes.crmCluster}>
        {workflow.crmIntegrations.map(integration => (
          <AlineTooltip
            key={integration}
            arrow
            placement="bottom"
            slotProps={{ popper: { modifiers: [{ enabled: false, name: 'flip' }] } }}
            title={getTemplatePublishedInTooltip(integration)}
          >
            <div className={classes.crmChip}>
              <CrmIntegrationIcon className={classes.crmIcon} integration={integration} />
            </div>
          </AlineTooltip>
        ))}
        <div className={classes.crmDivider} />
      </div>
    </Collapse>
  );
}

/**
 * Board, status, then labels — rendered inside the 960px content container.
 */
export const WorkflowHeaderMeta = React.memo(function WorkflowHeaderMeta({
  workflowId,
}: WorkflowHeaderProps) {
  const { classes } = useStyles();
  const links = useWorkflowDetailLinks(workflowId);

  if (!links.workflow) {
    return (
      <div className={classes.metaRow}>
        <div className={classes.labelsSkeleton}>
          <Skeleton height={24} sx={{ borderRadius: 3 }} variant="rectangular" width={64} />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.metaRow}>
      <BoardMetadata workflow={links.workflow} />
      <WorkflowLabelList
        numDisplayedLabels={4}
        popoverProps={{
          anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
          style: { transform: 'translateY(4px)' },
          transformOrigin: { horizontal: 'left', vertical: 'top' },
        }}
        workflow={links.workflow}
      />
    </div>
  );
});

function WorkflowTitleRowSkeleton() {
  const { classes } = useStyles();
  return (
    <div className={classes.titleRow}>
      <div className={classes.titleStart}>
        <Skeleton className={classes.backButton} height={32} variant="circular" width={32} />
        <Skeleton height={32} variant="text" width={200} />
      </div>
      <div className={classes.titleActions}>
        <div className={classes.assigneesSkeleton}>
          <Skeleton height={32} variant="circular" width={32} />
        </div>
      </div>
    </div>
  );
}

const useWorkflowNameStyles = makeStyles()(theme => ({
  nameDisplay: {
    fontSize: 20,
    fontWeight: 700,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameInput: {
    '& .MuiInputBase-input': {
      fontSize: 20,
      fontWeight: 700,
    },
    fontFamily: 'inherit',
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 'inherit',
    padding: 0,
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1),
    minWidth: 0,
  },
}));

function WorkflowName({ workflow }: { workflow: WorkflowDetailMeta }) {
  const { classes } = useWorkflowNameStyles();
  const [updateWorkflowName] = Apollo.useUpdateWorkflowNameMutation();
  const entityLabel =
    workflow.variant === Apollo.WorkflowVariant.Template ? 'Template' : 'Workflow';
  const noun = entityLabel.toLowerCase();

  const handleSave = useCallback(
    async (name: string) => {
      const trimmedName = name.trim();
      await updateWorkflowName({
        optimisticResponse: {
          __typename: 'Mutation',
          updateWorkflow: {
            __typename: 'WorkflowResponse',
            workflow: {
              __typename: 'Workflow',
              id: workflow.id,
              name: trimmedName,
            },
          },
        },
        variables: { input: { props: { name: trimmedName }, workflowId: workflow.id } },
      });
    },
    [updateWorkflowName, workflow.id]
  );

  return (
    <div className={classes.root}>
      <InlineEditField
        classes={{ display: classes.nameDisplay, input: classes.nameInput }}
        editTooltip={`Rename ${noun}`}
        iconSize={16}
        placeholder={`Type a ${noun} title...`}
        textPlaceholder={`Untitled ${entityLabel}`}
        value={workflow.name}
        onSave={handleSave}
      />
    </div>
  );
}
